import { Fragment, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

const canViewFeature = (userAccessLevel, requiredAccessLevel) => {
    const normalizedUserAccessLevel = userAccessLevel ?? 'public'
    const normalizedRequiredAccessLevel = requiredAccessLevel ?? 'off'

    if (normalizedRequiredAccessLevel === 'off') {
        return false
    }

    if (normalizedRequiredAccessLevel === 'public') {
        return true
    }

    if (normalizedRequiredAccessLevel === 'internal') {
        return normalizedUserAccessLevel === 'internal' || normalizedUserAccessLevel === 'dev'
    }

    if (normalizedRequiredAccessLevel === 'dev') {
        return normalizedUserAccessLevel === 'dev'
    }

    return false
}

const Feature = ({
    featureName,
    accepted,
    rejected = null,
    pollIntervalMs = 30000,
    redirectOnDenied = false,
    redirectTo = '/',
    onAccept = null,
    onReject = null
}) => {
    const axios = useAxiosPrivate()
    const { auth } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()

    const [requiredAccessLevel, setRequiredAccessLevel] = useState(null)
    const [hasCheckedAccess, setHasCheckedAccess] = useState(false)

    useEffect(() => {
        if (!featureName) {
            setRequiredAccessLevel(null)
            setHasCheckedAccess(false)
            return
        }

        let isMounted = true

        setHasCheckedAccess(false)

        const loadFeatureAccess = async () => {
            try {
                const response = await axios.post('/feature-flags/required-access-level', {
                    name: featureName,
                })

                if (isMounted) {
                    const result = response.data?.result
                    const featureExists = result?.feature_exists
                    const level = result?.required_access_level

                    if (featureExists === false || level === null || level === undefined || level === '') {
                        setRequiredAccessLevel(null)
                        setHasCheckedAccess(true)
                        return
                    }

                    setRequiredAccessLevel(level)
                    setHasCheckedAccess(true)
                }
            } catch (error) {
                console.error(error)
                if (isMounted) {
                    setRequiredAccessLevel(null)
                    setHasCheckedAccess(true)
                }
            }
        }

        loadFeatureAccess()

        const shouldPoll = Number.isFinite(pollIntervalMs) && pollIntervalMs > 0
        const intervalId = shouldPoll
            ? setInterval(() => {
                loadFeatureAccess()
            }, pollIntervalMs)
            : null

        return () => {
            isMounted = false

            if (intervalId) {
                clearInterval(intervalId)
            }
        }
    }, [axios, pollIntervalMs, featureName])

    const hasAccess = useMemo(() => {
        if (!hasCheckedAccess || !requiredAccessLevel) return false

        const userAccessLevel = auth?.user?.access_level ?? 'public'
        return canViewFeature(userAccessLevel, requiredAccessLevel)
    }, [auth?.user?.access_level, hasCheckedAccess, requiredAccessLevel])

    useEffect(() => {
        if (!redirectOnDenied) return
        if (!hasCheckedAccess) return
        if (hasAccess) return
        if (location.pathname === redirectTo) return

        navigate(redirectTo, {
            replace: true,
            state: { from: location },
        })
    }, [hasAccess, hasCheckedAccess, location, navigate, redirectOnDenied, redirectTo])

    if (hasAccess) {
        if (typeof onAccept === 'function') {
            onAccept()
        }
        return <Fragment>{accepted}</Fragment>
    }

    if (rejected || onReject) {
        if (typeof onReject === 'function') {
            onReject()
        }
        return <Fragment>{rejected}</Fragment>
    }

    return null
}

export default Feature